import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Gift, Users, Disc3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authHeaders, fmtDate } from "./adminApi";

const emptyOffer = { code: "", title: "", description: "", type: "percent", value: "", min_amount: "", min_nights: "", expires_at: "", active: true, is_welcome: false, on_wheel: false, wheel_weight: 1, single_use: true };

const valueLabel = { percent: "Discount (%)", fixed: "Discount ($)", freebie: "Value (not used)", free_night: "Value (auto: 1 night)" };

const offerSummary = (o) =>
  o.type === "percent" ? `${o.value}% off`
    : o.type === "fixed" ? `$${Number(o.value).toFixed(2)} off`
      : o.type === "free_night" ? `1 night free (min ${o.min_nights || 3} nights)`
        : "Free perk";

const OfferDialog = ({ open, onOpenChange, offer, onSaved }) => {
  const [form, setForm] = useState(emptyOffer);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(offer ? { ...offer } : emptyOffer);
  }, [offer, open]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const save = async () => {
    if (!form.code.trim() || !form.title.trim()) {
      toast.error("Code and title are required");
      return;
    }
    if (form.type !== "freebie" && form.type !== "free_night" && (form.value === "" || Number(form.value) <= 0)) {
      toast.error("Enter a discount value greater than 0");
      return;
    }
    setSaving(true);
    try {
      const url = offer ? `${API}/api/admin/offers/${offer.code}` : `${API}/api/admin/offers`;
      const res = await fetch(url, {
        method: offer ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ ...form, value: Number(form.value) || 0, min_amount: Number(form.min_amount) || 0, min_nights: Number(form.min_nights) || 0, wheel_weight: Number(form.wheel_weight) || 1 }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || "Could not save offer");
        return;
      }
      toast.success(offer ? "Offer updated" : "Offer created");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Could not save offer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{offer ? "Edit Offer" : "Create Offer"}</DialogTitle>
          <DialogDescription className="font-body text-xs">
            Guests enter the promo code in their cart. Percent/fixed codes discount the total; freebies add a perk note to the booking.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-body text-sm">Promo Code *</Label>
              <Input data-testid="offer-code" value={form.code} onChange={update("code")} placeholder="SUMMER20" disabled={Boolean(offer)} className="font-body uppercase" />
            </div>
            <div>
              <Label className="font-body text-sm">Type *</Label>
              <select data-testid="offer-type" value={form.type} onChange={update("type")} className="w-full h-9 rounded-md border border-input bg-white px-3 font-body text-sm">
                <option value="percent">Percent off</option>
                <option value="fixed">Fixed $ off</option>
                <option value="freebie">Free perk / attraction</option>
                <option value="free_night">Free night (min nights)</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Title *</Label>
            <Input data-testid="offer-title" value={form.title} onChange={update("title")} placeholder="20% Off Summer Stays" className="font-body" />
          </div>
          <div>
            <Label className="font-body text-sm">Description</Label>
            <Textarea data-testid="offer-description" value={form.description} onChange={update("description")} rows={2} className="font-body" placeholder="Shown to guests when they sign up or apply the code" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-body text-sm">{valueLabel[form.type]}</Label>
              <Input data-testid="offer-value" type="number" min="0" step="0.01" value={form.value} onChange={update("value")} disabled={form.type === "freebie" || form.type === "free_night"} className="font-body" />
            </div>
            <div>
              <Label className="font-body text-sm">Minimum Booking ($)</Label>
              <Input data-testid="offer-min-amount" type="number" min="0" step="0.01" value={form.min_amount} onChange={update("min_amount")} placeholder="0 = none" className="font-body" />
            </div>
          </div>
          {form.type === "free_night" && (
            <div>
              <Label className="font-body text-sm">Minimum Nights *</Label>
              <Input data-testid="offer-min-nights" type="number" min="1" value={form.min_nights} onChange={update("min_nights")} placeholder="3" className="font-body" />
            </div>
          )}
          <div>
            <Label className="font-body text-sm">Expires On (optional)</Label>
            <Input data-testid="offer-expires" type="date" value={form.expires_at} onChange={update("expires_at")} className="font-body" />
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="offer-welcome" checked={Boolean(form.is_welcome)} onCheckedChange={(v) => setForm({ ...form, is_welcome: v })} />
            <span className="font-body text-sm text-foreground">Welcome offer (given to new visitors in the sign-up popup)</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="offer-on-wheel" checked={Boolean(form.on_wheel)} onCheckedChange={(v) => setForm({ ...form, on_wheel: v })} />
            <span className="font-body text-sm text-foreground">On the prize wheel</span>
            {form.on_wheel && (
              <div className="flex items-center gap-2 ml-auto">
                <Label className="font-body text-xs text-muted-foreground">Odds weight</Label>
                <Input data-testid="offer-wheel-weight" type="number" min="1" value={form.wheel_weight} onChange={update("wheel_weight")} className="font-body w-20 h-8" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="offer-single-use" checked={Boolean(form.single_use)} onCheckedChange={(v) => setForm({ ...form, single_use: v })} />
            <span className="font-body text-sm text-foreground">Single use per guest (one redemption per email)</span>
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="offer-active" checked={Boolean(form.active)} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <span className="font-body text-sm text-foreground">Active</span>
          </div>
          <Button data-testid="offer-save" onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-white font-body rounded-full">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const WheelOddsPanel = ({ offers, onSaved }) => {
  const wheelOffers = offers.filter((o) => o.on_wheel && o.active);
  const [weights, setWeights] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    setWeights(Object.fromEntries(wheelOffers.map((o) => [o.code, o.wheel_weight || 1])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  if (wheelOffers.length === 0) return null;
  const total = wheelOffers.reduce((s, o) => s + Math.max(Number(weights[o.code]) || 1, 1), 0);

  const saveWeight = async (offer) => {
    setSaving(offer.code);
    try {
      const res = await fetch(`${API}/api/admin/offers/${offer.code}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ ...offer, wheel_weight: Math.max(Number(weights[offer.code]) || 1, 1) }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Odds updated for ${offer.code}`);
      onSaved();
    } catch {
      toast.error("Could not update odds");
    } finally {
      setSaving("");
    }
  };

  return (
    <div data-testid="wheel-odds-panel" className="rounded-xl border border-primary/25 bg-primary/[0.03] p-5">
      <h3 className="font-heading text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
        <Disc3 size={17} className="text-primary" /> Prize Wheel Odds
      </h3>
      <p className="font-body text-sm text-muted-foreground mb-4">
        Higher weight = won more often. The chance column shows each prize's real winning odds.
      </p>
      <div className="space-y-1.5">
        {wheelOffers.map((o) => {
          const w = Math.max(Number(weights[o.code]) || 1, 1);
          const pct = ((w / total) * 100).toFixed(1);
          const changed = w !== (o.wheel_weight || 1);
          return (
            <div key={o.code} data-testid={`wheel-odds-row-${o.code}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-border/60 bg-white px-4 py-2.5 font-body text-sm">
              <span className="font-heading font-semibold text-primary tracking-wider w-32 shrink-0">{o.code}</span>
              <span className="flex-1 min-w-[140px] text-foreground">{o.title}</span>
              <div className="flex items-center gap-2">
                <Label className="font-body text-xs text-muted-foreground">Weight</Label>
                <Input
                  data-testid={`wheel-odds-input-${o.code}`}
                  type="number" min="1" step="1"
                  value={weights[o.code] ?? 1}
                  onChange={(e) => setWeights({ ...weights, [o.code]: e.target.value })}
                  className="font-body w-20 h-8"
                />
              </div>
              <span data-testid={`wheel-odds-pct-${o.code}`} className="font-body text-sm font-semibold text-primary w-20 text-right">{pct}%</span>
              <Button
                data-testid={`wheel-odds-save-${o.code}`}
                size="sm"
                onClick={() => saveWeight(o)}
                disabled={!changed || saving === o.code}
                className="h-8 rounded-full bg-primary hover:bg-primary/90 text-white font-body text-xs disabled:opacity-40"
              >
                {saving === o.code ? <Loader2 size={13} className="animate-spin" /> : "Save"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const OffersTab = () => {
  const [offers, setOffers] = useState([]);
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, sRes] = await Promise.all([
        fetch(`${API}/api/admin/offers`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/subscribers`, { headers: authHeaders() }),
      ]);
      if (oRes.ok) setOffers(await oRes.json());
      if (sRes.ok) setSubs(await sRes.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const remove = async (code) => {
    if (!window.confirm(`Delete offer ${code}?`)) return;
    const res = await fetch(`${API}/api/admin/offers/${code}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      toast.success("Offer deleted");
      fetchAll();
    } else {
      toast.error("Could not delete offer");
    }
  };

  const toggleActive = async (o) => {
    const res = await fetch(`${API}/api/admin/offers/${o.code}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ ...o, active: !o.active }),
    });
    if (res.ok) fetchAll();
    else toast.error("Could not update offer");
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div data-testid="admin-offers-tab" className="space-y-10">
      <WheelOddsPanel offers={offers} onSaved={fetchAll} />
      <div>
        <div className="flex items-center justify-between mb-5">
          <p className="font-body text-sm text-muted-foreground">{offers.length} promo codes · guests apply them in the cart</p>
          <Button data-testid="add-offer-button" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full">
            <Plus size={16} className="mr-1" /> Create Offer
          </Button>
        </div>
        <div className="space-y-2">
          {offers.map((o) => (
            <div key={o.code} data-testid={`offer-row-${o.code}`} className={`flex flex-wrap items-center gap-4 rounded-lg border p-4 ${o.active ? "border-border/60 bg-white" : "border-border/40 bg-muted/40 opacity-70"}`}>
              <span className="font-heading text-base font-semibold text-primary tracking-wider">{o.code}</span>
              <div className="flex-1 min-w-[180px]">
                <p className="font-body text-sm text-foreground">{o.title}</p>
                <p className="font-body text-xs text-muted-foreground">
                  {offerSummary(o)}
                  {o.min_amount > 0 ? ` · min $${o.min_amount}` : ""}
                  {o.expires_at ? ` · expires ${fmtDate(o.expires_at)}` : ""}
                  {o.single_use !== false ? " · 1 use/guest" : ""}
                </p>
              </div>
              {o.is_welcome && (
                <span className="font-body text-[10px] uppercase tracking-wider bg-accent/10 text-accent px-2 py-1 rounded-full flex items-center gap-1">
                  <Gift size={11} /> Welcome
                </span>
              )}
              {o.on_wheel && (
                <span data-testid={`offer-wheel-badge-${o.code}`} className="font-body text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-2 py-1 rounded-full">
                  🎡 Wheel ×{o.wheel_weight || 1}
                </span>
              )}
              <Switch data-testid={`offer-toggle-${o.code}`} checked={Boolean(o.active)} onCheckedChange={() => toggleActive(o)} />
              <button data-testid={`offer-edit-${o.code}`} onClick={() => { setEditing(o); setDialogOpen(true); }} className="text-muted-foreground hover:text-primary p-1" aria-label="Edit">
                <Pencil size={16} />
              </button>
              <button data-testid={`offer-delete-${o.code}`} onClick={() => remove(o.code)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
          <Users size={17} className="text-primary" /> Subscribers {subs ? `(${subs.count})` : ""}
        </h3>
        <p className="font-body text-sm text-muted-foreground mb-4">Visitors who signed up through the welcome popup — your marketing list.</p>
        {!subs || subs.subscribers.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground">No subscribers yet.</p>
        ) : (
          <div className="space-y-1.5" data-testid="subscribers-list">
            {subs.subscribers.map((s) => (
              <div key={s.email} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-white px-4 py-2.5 font-body text-sm">
                <span className="text-foreground">{s.name || "—"}</span>
                <span className="text-muted-foreground">{s.email}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <OfferDialog open={dialogOpen} onOpenChange={setDialogOpen} offer={editing} onSaved={fetchAll} />
    </div>
  );
};
